import {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useContext,
  use,
} from "react";
import { FaTrophy } from "react-icons/fa";
import { BarChart2, Share2, Clock } from "lucide-react";
import AdventurePath from "./AdventurePath";
import axios from "axios";
import dayjs from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
//import toast from 'react-hot-toast';
dayjs.extend(isSameOrBefore);
import ChallengeInviteCard from "./ChallengeInviteCard";
import { useNavigate } from "react-router-dom";
import { useQuiz } from "../contexts/QuizContext";
import { toast } from "react-toastify";
import SimpleThreeDotsLoader from "./SimpleThreeDotsLoader";
import trophyIcon from "/assets/images/trophy.png";
import shareGeneric from "/assets/images/share-generic.png";
import instagramIcon from "/assets/images/insta.png";
import whatsappIcon from "/assets/images/whatsapp.png";
import snapchatIcon from "/assets/images/snap.png";
import facebookIcon from "/assets/images/fb.png";
import { useLeaderboard } from "../contexts/LeaderboardContext";
import RateLimitModal from "./RateLimitModal";
import { useLocation } from "react-router-dom";
import ShieldProgressBar from "./common/ShieldProgressBar";
import { UserContext } from "../contexts/UserContext";
import {
  JOURNEY_MODAL_FLAG,
  ONBOARDING_CHECKLIST,
} from "../constants/storageKeys";
import {
  getStoredLearningSubject,
  persistLearningSubjectSelection,
} from "../utils/learningSubjectStorage";

import {
  RATE_LIMIT_FALLBACK_MESSAGE,
  extractRateLimitMessageFromResponse,
} from "../utils/rateLimit";

const baseUrl = import.meta.env.VITE_APP_BASE_URL;
const GOAL_RATE_LIMIT_SUPPORT_MESSAGE =
  "You've reached the current limit for goal quizzes. Please wait a few minutes before trying again or contact support if you need a higher limit.";
const SHIELD_GOALS = [
  { id: "bronze", label: "Bronze Shield", percent: 20 },
  { id: "silver", label: "Silver Shield", percent: 45 },
  { id: "gold", label: "Gold Shield", percent: 70 },
  { id: "diamond", label: "Diamond Shield", percent: 95 },
];
const LEVEL_BADGES = {
  1: { label: "Foundation Builder", description: "Lock in the basics" },
  2: { label: "Concept Explorer", description: "Apply ideas with confidence" },
  3: { label: "Skill Challenger", description: "Tackle tougher problems" },
  4: { label: "Exam Champion", description: "Maintain peak mastery" },
};
const LEARNING_LEVEL_META_FIELDS = [
  "subject",
  "chapter",
  "level",
  "lo",
  "shield_percentage",
  "shieldpercentage",
];

const ordinalSuffix = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) {
    return "th";
  }
  const remainder100 = number % 100;
  if (remainder100 >= 11 && remainder100 <= 13) {
    return "th";
  }
  switch (number % 10) {
    case 1:
      return "st";
    case 2:
      return "nd";
    case 3:
      return "rd";
    default:
      return "th";
  }
};

const getGradeKeyForLearning = (grade) => {
  if (grade === null || grade === undefined) {
    return "10th Grade";
  }

  const gradeStr = String(grade).trim();
  if (!gradeStr.length) {
    return "10th Grade";
  }

  if (/grade/i.test(gradeStr)) {
    return gradeStr;
  }

  if (!/^\d+$/.test(gradeStr)) {
    return gradeStr;
  }

  return `${gradeStr}${ordinalSuffix(gradeStr)} Grade`;
};

const getChapterNameFromLevelEntry = (entry) => {
  if (!entry) {
    return null;
  }

  if (typeof entry === "string") {
    return entry;
  }

  const chapterValue = entry?.chapter ?? entry?.subject?.chapter ?? entry;

  if (typeof chapterValue === "string") {
    return chapterValue;
  }

  if (typeof chapterValue === "object" && chapterValue !== null) {
    return chapterValue?.name || chapterValue?.title || null;
  }

  return null;
};

const clampPercent = (value) => {
  const numeric = Number(value);
  if (Number.isNaN(numeric)) {
    return 0;
  }
  return Math.min(100, Math.max(0, numeric));
};

const getProgressPercentFromLevel = (levelValue) => {
  const numericLevel = Number(levelValue);
  if (!Number.isFinite(numericLevel)) {
    return null;
  }
  const clampedLevel = Math.min(Math.max(numericLevel, 0), 4);
  return (clampedLevel / 4) * 100;
};

const JourneyModal = ({ open, onClose, onNavigate, checklist }) => {
  const { userData } = useContext(UserContext);

  const showSelfAssessment = checklist?.self_assessment === false;
  const showEvaluatedSheet = checklist?.evaluated_answersheet === false;
  const hasVisibleSteps = showSelfAssessment || showEvaluatedSheet;

  if (!open || !hasVisibleSteps) return null;

  const userName = userData?.name?.trim() || "Mathlete";
  const gridColumns =
    showSelfAssessment && showEvaluatedSheet
      ? "sm:grid-cols-2"
      : "sm:grid-cols-1";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 px-4 py-8 backdrop-blur-sm">
      <div className="relative w-full max-w-xl rounded-3xl bg-white p-8 shadow-2xl">
        <button
          type="button"
          aria-label="Close"
          onClick={onClose}
          className="absolute right-4 top-4 rounded-full p-2 text-slate-500 transition hover:bg-slate-100"
        >
          <span className="text-lg leading-none">X</span>
        </button>
        <div className="space-y-4">
          <p className="text-sm font-semibold uppercase tracking-[0.2em] text-primary">
            Welcome!
          </p>
          <h2 className="text-3xl font-bold text-slate-900">
            Start your journey - {userName}!
          </h2>
          <p className="text-base text-slate-600">
            Help Aanya learn how you study so she can plan practice made just
            for you. Choose one quick step to share a little more.
          </p>
          <div className={`grid gap-4 ${gridColumns}`}>
            {showSelfAssessment && (
              <button
                type="button"
                onClick={() => onNavigate("/assessment")}
                className="flex w-full flex-col gap-2 rounded-2xl bg-primary p-5 text-left text-white shadow-lg shadow-primary/30 transition hover:-translate-y-0.5 hover:bg-primary-dark"
              >
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-white/80"></p>
                <p className="text-xl font-semibold">Self Assessment</p>
                <p className="text-sm text-white/90">
                  - a short check-up so Aanya knows where to start.
                </p>
              </button>
            )}
            {showEvaluatedSheet && (
              <button
                type="button"
                onClick={() => onNavigate("/practice/evaluateAnswerSheet")}
                className="flex w-full flex-col gap-2 rounded-2xl bg-primary p-5 text-left text-white shadow-lg shadow-slate-400/40 transition hover:-translate-y-0.5 hover:bg-slate-800"
              >
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-white/70"></p>
                <p className="text-xl font-semibold">Upload Answer Sheet</p>
                <p className="text-sm text-white/85">
                  - share your recent answers so we can tailor the next plan.
                </p>
              </button>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-sm font-semibold text-slate-500 underline-offset-4 hover:underline"
          >
            Maybe later
          </button>
        </div>
      </div>
    </div>
  );
};

export default function Leaderboard() {
  const { userData, fetchUserProfile } = useContext(UserContext) || {};
  const [isUserLoading, setIsUserLoading] = useState(true);
  const onboardingChecklist = userData?.onboarding_checklist;
  const [shouldShowNewHomeUI, setShouldShowNewHomeUI] = useState(false);
  useEffect(() => {
    setShouldShowNewHomeUI(
      onboardingChecklist.self_assessment ||
        onboardingChecklist.evaluated_answersheet
    );
  }, [userData?.onboarding_checklist]);
  const [showJourneyModal, setShowJourneyModal] = useState(false);
  const [journeyChecklist, setJourneyChecklist] = useState(null);
  const [activeTab, setActiveTab] = useState("algebra");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [leaderboardData, setLeaderboardData] = useState([]);
  const [todaysGoals, setTodaysGoals] = useState([]);
  const [todayGoalProgress, setTodayGoalProgress] = useState(0);
  const [weeklyGoals, setWeeklyGoals] = useState([]);
  const [startingGoalId, setStartingGoalId] = useState(null);
  const [visibleCount, setVisibleCount] = useState(10); // Initial visible students
  const [completedTasks, setCompletedTasks] = useState([]);
  const [isLoadingProgressSummary, setIsLoadingProgressSummary] =
    useState(true);
  const [progressSummaryError, setProgressSummaryError] = useState(null);
  const [challengeCompletedCount, setChallengeCompletedCount] = useState(0);
  const [questionAnsweredCount, setQuestionAnsweredCount] = useState(0);
  const [streak, setStreak] = useState([]);
  const [isLoadingGoals, setIsLoadingGoals] = useState(true);
  const [goalDataError, setGoalDataError] = useState(null);
  const [isRateLimitModalOpen, setIsRateLimitModalOpen] = useState(false);
  const [rateLimitServerMessage, setRateLimitServerMessage] = useState("");
  const userName = userData?.name?.trim() || "Student";
  const profilePhoto =
    userData?.profilePhoto || "/assets/avatars/defaultprofile.png";

  useEffect(() => {
    let isMounted = true;

    const loadUserProfile = async () => {
      try {
        if (typeof fetchUserProfile === "function") {
          await fetchUserProfile();
        }
      } finally {
        if (isMounted) {
          setIsUserLoading(false);
        }
      }
    };

    loadUserProfile();

    return () => {
      isMounted = false;
    };
    // We intentionally fetch the profile once when the leaderboard loads.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // useEffect(() => {
  //   const hasCompletedOnboarding = Boolean(
  //     onboardingChecklist?.self_assessment ||
  //       onboardingChecklist?.evaluated_answersheet
  //   );
  //   setShouldShowNewHomeUI(hasCompletedOnboarding);
  // }, [onboardingChecklist]);

  const location = useLocation();
  const userGrade = userData?.grade;
  const gradeKey = getGradeKeyForLearning(userGrade);
  const learningCurrentLevel = userData?.learning_current_level || {};
  const gradeLevelData =
    gradeKey && typeof learningCurrentLevel === "object"
      ? learningCurrentLevel[gradeKey]
      : undefined;
  const learningEnabledSubjects = useMemo(() => {
    if (!gradeLevelData || typeof gradeLevelData !== "object") {
      return [];
    }

    const normalizedSubjects = Object.entries(gradeLevelData || {})
      .filter(([key, value]) => {
        if (typeof key !== "string") {
          return false;
        }
        const trimmedKey = key.trim();
        if (!trimmedKey.length) {
          return false;
        }
        const normalizedKey = trimmedKey.toLowerCase();
        if (LEARNING_LEVEL_META_FIELDS.includes(normalizedKey)) {
          return false;
        }
        if (value === null || typeof value === "undefined") {
          return false;
        }
        return typeof value === "object";
      })
      .map(([subject]) => subject)
      .filter(
        (subject) => typeof subject === "string" && subject.trim().length > 0
      );

    if (normalizedSubjects.length > 0) {
      return normalizedSubjects;
    }

    if (
      typeof gradeLevelData.subject === "string" &&
      gradeLevelData.subject.trim().length > 0 &&
      (gradeLevelData.chapter ||
        gradeLevelData.level ||
        gradeLevelData.lo ||
        gradeLevelData.shield_percentage ||
        gradeLevelData.shieldPercentage)
    ) {
      return [gradeLevelData.subject.trim()];
    }

    return [];
  }, [gradeLevelData]);
  const [selectedLearningSubject, setSelectedLearningSubject] = useState(() => {
    if (
      !Array.isArray(learningEnabledSubjects) ||
      learningEnabledSubjects.length === 0
    ) {
      return getStoredLearningSubject();
    }

    const fallbackSubject = learningEnabledSubjects[0] || null;
    const storedSubject = getStoredLearningSubject();
    if (storedSubject && learningEnabledSubjects.includes(storedSubject)) {
      return storedSubject;
    }

    if (fallbackSubject) {
      persistLearningSubjectSelection(fallbackSubject);
    }
    return fallbackSubject;
  });
  useEffect(() => {
    if (
      !Array.isArray(learningEnabledSubjects) ||
      !learningEnabledSubjects.length
    ) {
      setSelectedLearningSubject(null);
      return;
    }
    setSelectedLearningSubject((current) => {
      if (current && learningEnabledSubjects.includes(current)) {
        return current;
      }

      const storedSubject = getStoredLearningSubject();
      if (storedSubject && learningEnabledSubjects.includes(storedSubject)) {
        return storedSubject;
      }

      const fallbackSubject = learningEnabledSubjects[0] || null;
      if (fallbackSubject) {
        persistLearningSubjectSelection(fallbackSubject);
      }
      return fallbackSubject;
    });
  }, [learningEnabledSubjects]);
  useEffect(() => {
    if (!selectedLearningSubject) {
      return;
    }
    persistLearningSubjectSelection(selectedLearningSubject);
  }, [selectedLearningSubject]);
  const getSubjectEntryFromLearningLevel = useCallback(
    (subject) => {
      if (!gradeLevelData || !subject || typeof subject !== "string") {
        return null;
      }
      const trimmedSubject = subject.trim();
      if (!trimmedSubject.length) {
        return null;
      }

      if (
        Object.prototype.hasOwnProperty.call(gradeLevelData, trimmedSubject)
      ) {
        return gradeLevelData[trimmedSubject];
      }

      const normalizedSubject = trimmedSubject.toLowerCase();
      const matchedKey = Object.keys(gradeLevelData).find((key) => {
        if (typeof key !== "string") {
          return false;
        }
        return key.trim().toLowerCase() === normalizedSubject;
      });

      if (matchedKey) {
        return gradeLevelData[matchedKey];
      }

      const singleSubjectMatch =
        typeof gradeLevelData.subject === "string" &&
        gradeLevelData.subject.trim().toLowerCase() === normalizedSubject;

      if (singleSubjectMatch) {
        return gradeLevelData;
      }

      return null;
    },
    [gradeLevelData]
  );
  const selectedLearningSubjectData = useMemo(() => {
    if (!selectedLearningSubject) {
      return null;
    }
    return getSubjectEntryFromLearningLevel(selectedLearningSubject);
  }, [getSubjectEntryFromLearningLevel, selectedLearningSubject]);
  const selectedLearningChapter = getChapterNameFromLevelEntry(
    selectedLearningSubjectData
  );
  const rawLevelValue = selectedLearningSubjectData?.level;
  const parsedLevelValue =
    rawLevelValue === null || typeof rawLevelValue === "undefined"
      ? null
      : Number(rawLevelValue);
  const selectedLevelValue = Number.isFinite(parsedLevelValue)
    ? parsedLevelValue
    : null;
  const derivedProgressFromLevel =
    selectedLevelValue !== null
      ? getProgressPercentFromLevel(selectedLevelValue)
      : null;
  const selectedShieldPercent =
    derivedProgressFromLevel !== null
      ? derivedProgressFromLevel
      : clampPercent(
          selectedLearningSubjectData?.shield_percentage ??
            selectedLearningSubjectData?.shieldPercentage ??
            0
        );
  // Get the actual shield_percentage value for ShieldProgressBar
  const shieldPercentageValue = clampPercent(
    selectedLearningSubjectData?.shield_percentage ??
      selectedLearningSubjectData?.shieldPercentage ??
      0
  );
  const nextShieldGoal =
    SHIELD_GOALS.find((tier) => shieldPercentageValue < (tier.percent || 0)) ||
    null;
  const nextShieldLabel = nextShieldGoal
    ? nextShieldGoal.label
    : "All shields unlocked";
  const selectedLevelBadge =
    selectedLevelValue != null ? LEVEL_BADGES[selectedLevelValue] : null;
  const selectedLearningObjective =
    typeof selectedLearningSubjectData?.lo !== "undefined"
      ? selectedLearningSubjectData?.lo
      : null;
  const shouldShowLearningProgress =
    learningEnabledSubjects.length > 0 && Boolean(selectedLearningSubjectData);
  const userFirstName =
    userData?.name?.trim()?.split(/\s+/)[0] ||
    userData?.fullName?.trim()?.split(/\s+/)[0] ||
    "Learner";
  const adaptivePracticeChapter =
    typeof selectedLearningChapter === "string" &&
    selectedLearningChapter.trim().length
      ? selectedLearningChapter
      : "your current chapter";

  // useEffect(() => {
  //   if (shouldShowNewHomeUI) {
  //     return;
  //   }
  //   if (typeof window === "undefined") return;

  //   const readChecklist = () => {
  //     try {
  //       const rawChecklist = localStorage.getItem(ONBOARDING_CHECKLIST);
  //       return rawChecklist ? JSON.parse(rawChecklist) : null;
  //     } catch (error) {
  //       console.error("Unable to parse onboarding checklist:", error);
  //       return null;
  //     }
  //   };

  //   const evaluateJourneyModalState = async () => {
  //     const checklist = readChecklist();
  //     setJourneyChecklist(checklist);

  //     const hasPendingSteps =
  //       checklist?.self_assessment === false ||
  //       checklist?.evaluated_answersheet === false;
  //     const shouldShowFlag =
  //       (await localStorage.getItem(JOURNEY_MODAL_FLAG)) === "true";

  //     if (hasPendingSteps && shouldShowFlag) {
  //       setShowJourneyModal(true);
  //       localStorage.setItem(JOURNEY_MODAL_FLAG, "false");
  //     } else {
  //       setShowJourneyModal(false);
  //       if (!hasPendingSteps) {
  //         localStorage.setItem(JOURNEY_MODAL_FLAG, "false");
  //       }
  //     }
  //   };

  //   evaluateJourneyModalState();
  //   window.addEventListener("userLoggedIn", recheckJourneyState);
  //   window.addEventListener("storage", recheckJourneyState);

  //   return () => {
  //     window.removeEventListener("userLoggedIn", recheckJourneyState);
  //     window.removeEventListener("storage", recheckJourneyState);
  //   };
  // }, [shouldShowNewHomeUI]);

  const navigate = useNavigate();
  const [isJoining, setIsJoining] = useState(false);

  const markJourneyModalHandled = () => {
    try {
      localStorage.setItem(JOURNEY_MODAL_FLAG, "false");
    } catch (error) {
      console.error("Unable to update journey modal flag:", error);
    }
  };

  const handleJourneyModalClose = () => {
    setShowJourneyModal(false);
    markJourneyModalHandled();
  };

  const handleJourneyModalNavigate = (path) => {
    handleJourneyModalClose();
    navigate(path);
  };

  const handleSolveDoubt = () => {
    setShouldShowNewHomeUI(true);
    navigate("/tutor?enableWorkbookmode=true&tab=pastYear");
  };

  const handleCheckWork = () => {
    setShouldShowNewHomeUI(true);
    navigate("/practice/evaluateAnswerSheet?mode=new");
  };

  const handleGoToLearn = () => {
    navigate("/new-learn");
  };

  const {
    leaderboard,
    loading: isLoadingLeaderboard,
    error: leaderboardError,
    refetchLeaderboard,
  } = useLeaderboard();

  const { saveQuizState } = useQuiz();
  const showRateLimitModal = useCallback((message) => {
    setRateLimitServerMessage(message?.trim() || RATE_LIMIT_FALLBACK_MESSAGE);
    setIsRateLimitModalOpen(true);
  }, []);
  const handleRateLimitModalClose = useCallback(() => {
    setIsRateLimitModalOpen(false);
    setRateLimitServerMessage("");
  }, []);

  const [showSharePopover, setShowSharePopover] = useState(null);
  const buildShareKey = (section, index) => `${section}-${index}`;
  const getShareUrl = (platform, goal) => {
    const text = encodeURIComponent(
      `I completed my goal: ${goal.subject} test on ${dayjs(
        goal.testDate
      ).format("dddd")}!`
    );
    const url = encodeURIComponent(window.location.href);
    switch (platform) {
      case "instagram":
        return `https://www.instagram.com/?text=${text}`;
      case "whatsapp":
        return `https://wa.me/?text=${text}%20${url}`;
      case "snapchat":
        return `https://www.snapchat.com/`;
      case "facebook":
        return `https://www.facebook.com/sharer/sharer.php?u=${url}&quote=${text}`;
      default:
        return `mailto:?subject=I completed my goal!&body=${text}%20${url}`;
    }
  };

  // Fetch leaderboard data
  useEffect(() => {
    const fetchLeaderboard = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(`${baseUrl}/v2/leaderboard`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });
        setLeaderboardData(response.data.leaderboard || response.data || []);
      } catch (err) {
        console.error("Error fetching leaderboard:", err);
        setError(err.message);
        setLeaderboardData([]);
      }
    };

    fetchLeaderboard();
  }, [baseUrl]);

  // Update loading and error state based on leaderboard context
  useEffect(() => {
    setLoading(isLoadingLeaderboard);
    setError(leaderboardError);
  }, [isLoadingLeaderboard, leaderboardError]);

  const handleJoinChallenge = async (challengeId) => {
    try {
      setIsJoining(true);
      // Removed duplicate toast - ChallengeInviteCard already shows success message
      setTimeout(() => {
        setIsJoining(false);
        navigate(`/challenge/waiting/${challengeId}`);
      }, 1500); // 1.5 seconds delay
    } catch (err) {
      setIsJoining(false);
      toast.error("Failed to join challenge", err);
    }
  };
  const [pendingChallenges, setPendingChallenges] = useState([]);
  const userId = localStorage.getItem("userId");
  useEffect(() => {
    async function fetchPendingChallenges() {
      const res = await axios.get(
        `${baseUrl}/v2/user/${userId}/pending-challenges`
      );

      setPendingChallenges(res.data.challenges); // ✅ set the array directly
      console.log(res.data.challenges, "fetched"); // ✅ log immediately if needed
    }
    fetchPendingChallenges();
  }, []);

  // Fetch today's and weekly goals (shared logic with MyGoals)
  useEffect(() => {
    const processGoalsData = (goals = []) => {
      const today = dayjs().startOf("day");
      const tomorrow = today.add(1, "day");
      const nextWeek = today.add(7, "day");
      const todayList = [];
      const weekList = [];
      let todayCompleted = 0;

      goals.forEach((goal) => {
        if (!goal) return;
        const testDate = dayjs(goal.testDate).startOf("day");
        const isDueToday = testDate.isSame(today, "day");
        const isReminderForTomorrow =
          goal.reminder === "1 Day Before" && testDate.isSame(tomorrow, "day");

        if (isDueToday || isReminderForTomorrow) {
          todayList.push(goal);
          if (goal.completed) todayCompleted += 1;
        } else if (
          testDate.isAfter(tomorrow, "day") &&
          testDate.isSameOrBefore(nextWeek, "day")
        ) {
          weekList.push(goal);
        }
      });

      const todayProgress = todayList.length
        ? (todayCompleted / todayList.length) * 100
        : 0;

      setTodaysGoals(todayList);
      setWeeklyGoals(weekList);
      setTodayGoalProgress(todayProgress);
    };

    const fetchGoals = async () => {
      setIsLoadingGoals(true);
      setGoalDataError(null);
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          throw new Error("You need to log in to view goals.");
        }

        const response = await axios.get(`${baseUrl}/v2/get-goals`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        const goals = response.data.goals || [];
        window.goalsData = goals;
        processGoalsData(goals);
        setGoalDataError(null);
      } catch (error) {
        console.log("=== Get Goals API Error ===");
        console.log("Error:", error.message);
        if (error.response) {
          console.log("Status:", error.response.status);
          console.log("Data:", error.response.data);
          console.log("Headers:", error.response.headers);
        }
        setGoalDataError("Unable to load today's goals. Please try again.");
        setTodaysGoals([]);
        setWeeklyGoals([]);
        setTodayGoalProgress(0);
      } finally {
        setIsLoadingGoals(false);
      }
    };

    if (window.goalsData && window.goalsData.length > 0) {
      processGoalsData(window.goalsData);
      setGoalDataError(null);
      setIsLoadingGoals(false);
    } else {
      fetchGoals();
    }
  }, [baseUrl, location.key]);

  // Fetch weekly progress summary for recent achievements
  useEffect(() => {
    const fetchWeeklyProgressSummary = async () => {
      setIsLoadingProgressSummary(true);
      setProgressSummaryError(null);

      try {
        const token = localStorage.getItem("token");
        if (!token) {
          throw new Error("Authentication required");
        }

        const response = await axios.get(
          `${baseUrl}/progress/progress2/summary?period=weekly`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const summaryData = response.data || {};
        if (Array.isArray(summaryData.completed_task)) {
          setCompletedTasks(summaryData.completed_task);
        } else {
          setCompletedTasks([]);
        }

        // Extract effort data
        const effort = summaryData.effort || {};
        setChallengeCompletedCount(effort.challenge_completed_count || 0);
        setQuestionAnsweredCount(effort.question_answered_count || 0);
        setStreak(Array.isArray(effort.streak) ? effort.streak : []);
      } catch (error) {
        console.error("Error fetching weekly progress summary:", error);
        setProgressSummaryError("Unable to load recent achievements.");
        setCompletedTasks([]);
        setChallengeCompletedCount(0);
        setQuestionAnsweredCount(0);
        setStreak([]);
      } finally {
        setIsLoadingProgressSummary(false);
      }
    };

    fetchWeeklyProgressSummary();
  }, [baseUrl]);

  // Get initials from name for avatar
  const getInitials = (name) => {
    if (!name) return "??";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase();
  };

  // Analyze streak data
  const getStreakInfo = (streakArray) => {
    if (!Array.isArray(streakArray) || streakArray.length === 0) {
      return null;
    }

    // Helper to get count from day (handles both object and number formats)
    const getDayCount = (day) => {
      if (typeof day === "number") return day;
      if (typeof day === "object" && day !== null) {
        return day.count || day.activity || 0;
      }
      return 0;
    };

    // Count days with activity > 0
    const activeDays = streakArray.filter((day) => getDayCount(day) > 0).length;

    // Check for continuous streak from the end (most recent days)
    let continuousDays = 0;
    for (let i = streakArray.length - 1; i >= 0; i--) {
      if (getDayCount(streakArray[i]) > 0) {
        continuousDays++;
      } else {
        break;
      }
    }

    return {
      activeDays,
      continuousDays,
      totalDays: streakArray.length,
    };
  };

  const streakInfo = useMemo(() => getStreakInfo(streak), [streak]);
  const hasRecentWins = useMemo(() => {
    if (challengeCompletedCount > 0 || questionAnsweredCount > 0) {
      return true;
    }
    if (!streakInfo) {
      return false;
    }
    return (
      (streakInfo.activeDays || 0) > 0 || (streakInfo.continuousDays || 0) > 0
    );
  }, [challengeCompletedCount, questionAnsweredCount, streakInfo]);

  // Get background color for avatar based on subject
  const getAvatarColor = (subject) => {
    const colors = {
      physics: "bg-blue-200 text-blue-700",
      algebra: "bg-purple-200 text-purple-700",
      mathematics: "bg-green-200 text-green-700",
      chemistry: "bg-yellow-200 text-yellow-700",
      biology: "bg-red-200 text-red-700",
    };
    return colors[subject?.toLowerCase()] || "bg-gray-200 text-gray-700";
  };

  const handleInviteButton = () => {
    const shareText =
      "Hey, I have been enjoying learning with Aanya AI. Let us play a challenge and learn together https://aanya.tech/login";
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(shareText)}`;
    window.open(whatsappUrl, "_blank");
  };

  const handleSetNewGoal = () => {
    navigate("/practice/mygoals/goals");
  };

  const handleStartAdaptivePractice = useCallback(() => {
    const params = new URLSearchParams();
    if (
      typeof selectedLearningSubject === "string" &&
      selectedLearningSubject.trim().length
    ) {
      params.set("subject", selectedLearningSubject);
    }
    if (
      typeof selectedLearningChapter === "string" &&
      selectedLearningChapter.trim().length
    ) {
      params.set("chapter", selectedLearningChapter);
    }
    const query = params.toString();
    if (query.length === 0) {
      navigate("/practice/sampleWorksheet");
      return;
    }
    navigate(`/practice/sampleWorksheet?${query}`);
  }, [navigate, selectedLearningSubject, selectedLearningChapter]);
  // Generate leaderboard message based on data

  const getLeaderboardMessage = (item) => {
    const name =
      item.name && item.name.length > 0
        ? item.name.charAt(0).toUpperCase() + item.name.slice(1).toLowerCase()
        : "Unknown User";

    if (item.previous_level && item.new_level && item.subject && item.topic) {
      return `${name} improved from ${item.previous_level} to ${item.new_level} in ${item.subject} - ${item.topic}`;
    }
    return `${name} made progress in ${item.subject} - ${item.topic}`;
  };

  const handleStartGoal = async (goalData) => {
    const goalId =
      typeof goalData === "object" && goalData !== null
        ? goalData._id || goalData.id
        : goalData;

    if (!goalId) {
      toast.error("Unable to start this goal. Please try again.");
      return;
    }

    const token = localStorage.getItem("token");
    if (!token) {
      toast.error("Please log in to start this goal.");
      return;
    }

    const subjectFromGoal =
      typeof goalData?.subject === "string" ? goalData.subject.trim() : "";
    const chapterFromGoal =
      typeof goalData?.topic === "string"
        ? goalData.topic.trim()
        : typeof goalData?.chapter === "string"
        ? goalData.chapter.trim()
        : "";
    const subjectFromSelection =
      typeof selectedLearningSubject === "string"
        ? selectedLearningSubject.trim()
        : "";
    const chapterFromSelection =
      typeof selectedLearningChapter === "string"
        ? selectedLearningChapter.trim()
        : "";

    const subjectForQuiz =
      subjectFromGoal ||
      subjectFromSelection ||
      (typeof goalData?.details === "string" ? goalData.details.trim() : "");
    const chapterForQuiz =
      chapterFromGoal ||
      chapterFromSelection ||
      (typeof goalData?.details === "string" ? goalData.details.trim() : "");

    if (
      !subjectForQuiz ||
      !chapterForQuiz ||
      typeof subjectForQuiz !== "string" ||
      typeof chapterForQuiz !== "string"
    ) {
      toast.error("We need the goal's subject and chapter to start.");
      return;
    }

    const requestPayload = {
      subject: subjectForQuiz.trim(),
      chapter: chapterForQuiz.trim(),
      num_questions: 5,
      question_type: "mcq",
      level: "developing",
      timer: 900,
      isPractice: true,
      goal_id: goalId,
    };

    try {
      if (goalId) {
        setStartingGoalId(goalId);
      }

      const response = await fetch(`${baseUrl}/v2/quiz/generate-new`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(requestPayload),
      });

      const rateLimitMessage = await extractRateLimitMessageFromResponse(
        response
      );
      if (rateLimitMessage) {
        const normalizedMessage =
          rateLimitMessage?.trim() || RATE_LIMIT_FALLBACK_MESSAGE;
        toast.warn(normalizedMessage);
        setError(normalizedMessage);
        showRateLimitModal(normalizedMessage);
        return;
      }

      const responseText = await response.text();
      if (!response.ok) {
        throw new Error(
          responseText?.trim() || "Failed to start quiz. Please try again."
        );
      }

      let parsedData = {};
      if (responseText) {
        try {
          parsedData = JSON.parse(responseText);
        } catch (parseError) {
          console.error("Unable to parse quiz response:", parseError);
          throw new Error("Received invalid data from the quiz generator.");
        }
      }

      const resultPayload =
        parsedData && typeof parsedData === "object" && parsedData.result
          ? parsedData.result
          : parsedData;

      const rawQuestionList = Array.isArray(resultPayload?.questions)
        ? resultPayload.questions
        : [];

      if (rawQuestionList.length === 0) {
        toast.info("No questions available for this goal.");
        return;
      }

      const normalizeOption = (option) => {
        if (typeof option === "string") return option;
        if (option && typeof option === "object") {
          return (
            option.option_text ||
            option.text ||
            option.label ||
            option.value ||
            String(option)
          );
        }
        return String(option || "");
      };

      const questionList = rawQuestionList.map((question) => {
        const normalizedQuestion = { ...question };

        if (Array.isArray(normalizedQuestion.options)) {
          normalizedQuestion.options =
            normalizedQuestion.options.map(normalizeOption);
        }

        if (Array.isArray(normalizedQuestion.choices)) {
          normalizedQuestion.choices =
            normalizedQuestion.choices.map(normalizeOption);
        }

        return normalizedQuestion;
      });

      const questionCount = Number.isFinite(
        Number(resultPayload?.question_count)
      )
        ? Math.max(Number(resultPayload.question_count), questionList.length)
        : questionList.length;

      const timerSeconds =
        Number.isFinite(Number(resultPayload?.timer)) &&
        Number(resultPayload.timer) > 0
          ? Number(resultPayload.timer)
          : requestPayload.timer;

      const userAnswersSeed = Array.from({ length: questionCount }, () => null);

      const normalizedResult = {
        ...resultPayload,
        subject: requestPayload.subject,
        topic: requestPayload.chapter,
        chapter: requestPayload.chapter,
        questions: questionList,
        question_count: questionCount,
        timer: timerSeconds,
      };

      const storedQuestions =
        parsedData && typeof parsedData === "object" && parsedData.result
          ? { ...parsedData, result: normalizedResult }
          : normalizedResult;

      const practiceTopicId = goalId || `generated-${Date.now()}`;

      const goalDetailsPayload = {
        subject: requestPayload.subject,
        topic: requestPayload.chapter,
        testDate: goalData?.testDate ?? null,
        reminder: goalData?.reminder ?? null,
        goalId: practiceTopicId,
      };

      saveQuizState({
        topicId: practiceTopicId,
        questions: storedQuestions,
        goalDetails: goalDetailsPayload,
        currentQuestionIndex: 0,
        selectedOption: null,
        score: 0,
        showExplanation: false,
        quizCompleted: false,
        leveledUp: false,
        goalStatus: goalData?.status ?? "",
        userAnswers: userAnswersSeed,
      });

      navigate(`/practice/goalquestions/${practiceTopicId}`, {
        state: {
          attemptId: normalizedResult?.attempt_id,
          goalId: goalId || null,
        },
      });
    } catch (error) {
      console.error("Error starting quiz:", error);
      const fallbackMessage =
        error?.message || "Failed to start quiz. Please try again.";
      toast.error(fallbackMessage);
      setError(fallbackMessage);
    } finally {
      setStartingGoalId(null);
    }
  };
  return (
    <>
      {isUserLoading && (
        <div className="h-[100dvh] flex flex-col items-center justify-center bg-white">
          <div className="flex items-end gap-2">
            <span
              className="w-3 h-3 rounded-full bg-[#1f2937] animate-bounce"
              style={{ animationDelay: "0ms" }}
            />
            <span
              className="w-3 h-3 rounded-full bg-[#1f2937] animate-bounce"
              style={{ animationDelay: "150ms" }}
            />
            <span
              className="w-3 h-3 rounded-full bg-[#1f2937] animate-bounce"
              style={{ animationDelay: "300ms" }}
            />
          </div>
          <p className="mt-3 text-sm text-gray-500">
            Loading your workspace...
          </p>
        </div>
      )}
      {!shouldShowNewHomeUI && (
        <div className="px-4 h-full overflow-y-scroll">
          <div className="h-full flex flex-col align-center">
            <div className="flex items-center gap-4 mb-6 h-2/10">
              <div className="w-12 h-12 md:w-16 md:h-16 rounded-full overflow-hidden bg-gray-200 flex-shrink-0">
                <img
                  src={profilePhoto}
                  alt={userName}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.src = "/assets/avatars/defaultprofile.png";
                  }}
                />
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
                  Hi, {userName}!
                </h1>
                <p className="text-sm md:text-base text-gray-500 mt-1">
                  What would you like to do first?
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mt-8 md:mt-12">
              <button
                onClick={handleSolveDoubt}
                className="group relative bg-white border-2 border-blue-500 rounded-2xl p-6 md:p-8 hover:shadow-lg transition-all duration-200 hover:-translate-y-1 text-left"
              >
                <div className="flex flex-col items-center text-center">
                  <div className="mb-4 md:mb-6">
                    <svg
                      width="80"
                      height="80"
                      viewBox="0 0 80 80"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="w-16 h-16 md:w-20 md:h-20"
                    >
                      <rect
                        x="35"
                        y="50"
                        width="10"
                        height="20"
                        fill="#2C2C2C"
                        rx="2"
                      />
                      <path
                        d="M40 20L45 35L60 40L45 45L40 60L35 45L20 40L35 35L40 20Z"
                        fill="#2C2C2C"
                      />
                      <circle cx="25" cy="25" r="3" fill="#FFD700" />
                      <circle cx="55" cy="25" r="3" fill="#FFD700" />
                      <circle cx="25" cy="55" r="3" fill="#FFD700" />
                      <circle cx="55" cy="55" r="3" fill="#FFD700" />
                      <circle cx="40" cy="15" r="2" fill="#FFD700" />
                      <circle cx="15" cy="40" r="2" fill="#FFD700" />
                      <circle cx="65" cy="40" r="2" fill="#FFD700" />
                    </svg>
                  </div>
                  <h2 className="text-lg md:text-xl font-bold text-gray-900 mb-2">
                    SOLVE MY DOUBT
                  </h2>
                  <p className="text-sm md:text-base text-gray-500">
                    Point camera or upload assignment
                  </p>
                </div>
              </button>

              <button
                onClick={handleCheckWork}
                className="group relative bg-white border-2 border-orange-500 rounded-2xl p-6 md:p-8 hover:shadow-lg transition-all duration-200 hover:-translate-y-1 text-left"
              >
                <div className="flex flex-col items-center text-center">
                  <div className="mb-4 md:mb-6">
                    <FaTrophy
                      className="w-16 h-16 md:w-20 md:h-20"
                      style={{ color: "#FFD700" }}
                    />
                  </div>
                  <h2 className="text-lg md:text-xl font-bold text-gray-900 mb-2">
                    CHECK MY WORK
                  </h2>
                  <p className="text-sm md:text-base text-gray-500">
                    Point camera or upload your worksheet
                  </p>
                </div>
              </button>
            </div>

            <div className="mt-8 md:mt-12 text-center">
              <button
                onClick={handleGoToLearn}
                className="text-sm md:text-base text-gray-500 hover:text-gray-700 transition-colors"
              >
                Just exploring?{" "}
                <span className="underline">Go to Learn Tab.</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {shouldShowNewHomeUI && (
        <div className="flex flex-col w-full bg-white rounded-lg">
          {shouldShowLearningProgress && (
            <div className="p-4 md:p-6">
              <div className="w-full flex justify-center mb-6 md:mb-8">
                <div className="flex gap-2 md:gap-3 overflow-x-auto -mx-2 px-2 md:mx-0 md:px-0 justify-center items-center bg-white rounded-3xl">
                  {learningEnabledSubjects.map((subject) => (
                    <button
                      key={subject}
                      onClick={() => {
                        setSelectedLearningSubject(subject);
                        persistLearningSubjectSelection(subject);
                      }}
                      className={`px-4 md:px-6 py-2 md:py-3 rounded-full text-sm md:text-base font-medium whitespace-nowrap transition-all duration-200 flex-shrink-0 ${
                        selectedLearningSubject === subject
                          ? "bg-[#0D99FF] text-white shadow-md"
                          : "bg-gray-50 text-gray-600 hover:bg-gray-100"
                      }`}
                    >
                      {subject}
                    </button>
                  ))}
                </div>
              </div>

              <div className="relative overflow-hidden rounded-3xl px-2  ">
                <div className="absolute inset-y-0 right-0 w-40  pointer-events-none"></div>
                <div className="relative z-10 flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                  <div>
                    <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 pt-2">
                      {selectedLearningChapter || "Choose your topic"}
                    </h1>
                    {/* {selectedLevelBadge && (
                  <p className="text-sm md:text-base text-gray-600 mt-2">
                    Level {selectedLevelValue} - {selectedLevelBadge.label}
                  </p>
                )}

                {selectedLevelBadge?.description && (
                  <p className="text-xs md:text-sm text-gray-500 mt-1">
                    {selectedLevelBadge.description}
                  </p>
                )} */}
                  </div>
                  {/* <div className="flex flex-col items-start md:items-end gap-2">
                <div className="text-xs md:text-sm uppercase tracking-[0.2em] text-gray-500">
                  Next Goal
                </div>
                <div className="text-lg md:text-2xl font-semibold text-gray-900">
                  {nextShieldLabel}
                </div>
                <div className="text-sm text-gray-500">
                  {Math.round(selectedShieldPercent)}% complete
                </div>
              </div> */}
                </div>
              </div>
            </div>
          )}

          <div className="flex flex-col px-4 md:px-6 ">
            {shouldShowLearningProgress && (
              <div className="relative z-10 mt-3 rounded-t-3xl bg-primary px-5">
                <ShieldProgressBar
                  progress={shieldPercentageValue}
                  mastery={shieldPercentageValue}
                  nextGoal={nextShieldLabel}
                  currentPage="home"
                />
              </div>
            )}
            <div className="bg-[#0D99FF] text-white  px-7 pb-7 rounded-b-3xl  flex flex-col justify-start">
              <div>
                <p className="text-2xl font-semibold ">
                  Welcome back, {userFirstName}!
                </p>
                <p className="text-sm md:text-base text-white/70">
                  You are close to the {nextShieldLabel}. Let's bridge the gap.
                </p>
              </div>
              <div className="flex flex-col w-full sm:w-auto sm:flex-row items-stretch sm:items-center gap-3">
                <button
                  onClick={handleStartAdaptivePractice}
                  className="bg-white text-[#0D99FF] font-semibold rounded-full px-6 py-3 text-sm md:text-base shadow-md hover:shadow-lg transition-all duration-200 text-center"
                >
                  Start Adaptive Practice
                </button>
                <div className="text-white/70 font-medium text-center text-sm md:text-base flex items-center justify-center gap-2">
                  <Clock className="w-4 h-4" />
                  15 mins session
                </div>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 md:p-6">
            <div className="border border-gray-300 rounded-lg p-4 space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-bold text-gray-800 text-lg">
                    Today's Schedule
                  </h3>
                  {!goalDataError &&
                    !isLoadingGoals &&
                    todaysGoals.length > 0 && (
                      <button
                        onClick={handleSetNewGoal}
                        className="px-4 py-2 rounded-full text-xs sm:text-sm text-white transition-colors min-w-[120px] text-center
                             bg-[#0D99FF] hover:bg-blue-600"
                      >
                        Set New Goal
                      </button>
                    )}
                </div>
              </div>
              {goalDataError ? (
                <div className="text-red-500 text-sm">{goalDataError}</div>
              ) : isLoadingGoals ? (
                <div className="flex justify-center py-4">
                  <SimpleThreeDotsLoader message="Fetching today's goals..." />
                </div>
              ) : todaysGoals.length === 0 ? (
                <div className="bg-gray-50 p-3 rounded-lg text-center flex flex-col items-center gap-3">
                  <p className="text-gray-500 text-sm max-w-xs">
                    No goals due today.
                  </p>
                  <button
                    onClick={handleSetNewGoal}
                    className="bg-[#0D99FF] hover:bg-blue-600 text-white font-semibold py-2 sm:py-3 px-8 sm:px-12 rounded-full shadow-lg text-sm sm:text-base transition-all duration-200"
                  >
                    Set New Goal
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  {[...todaysGoals]
                    .sort((a, b) => {
                      // Non-completed goals first (false/undefined comes before true)
                      if (a.completed && !b.completed) return 1;
                      if (!a.completed && b.completed) return -1;
                      return 0;
                    })
                    .map((goal, index) => {
                      const todayShareKey = buildShareKey("today", index);
                      return (
                        <div
                          key={goal._id || index}
                          className={`bg-white p-3 sm:p-4 rounded-xl  border flex flex-col sm:flex-row sm:justify-between ${
                            goal.completed
                              ? "border-2 border-green-400"
                              : "border-2 border-[#FF6B00]"
                          }`}
                        >
                          <div>
                            <h4 className="font-semibold text-sm sm:text-base">
                              {goal.subject} - {goal.topic}
                            </h4>
                            {goal.completed ? (
                              <div className="text-green-600 text-xs sm:text-sm">
                                Completed on{" "}
                                {new Date(goal.completed_at).toLocaleString(
                                  "en-US",
                                  {
                                    year: "numeric",
                                    month: "short",
                                    day: "2-digit",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                    hour12: true,
                                  }
                                )}
                              </div>
                            ) : (
                              <>
                                {(dayjs(goal.testDate)
                                  .startOf("day")
                                  .isSame(dayjs().startOf("day"), "day") ||
                                  dayjs(goal.testDate)
                                    .startOf("day")
                                    .isSame(
                                      dayjs().add(1, "day").startOf("day"),
                                      "day"
                                    )) && (
                                  <h1 className="text-sm sm:text-base text-amber-500 mb-0">
                                    {dayjs(goal.testDate)
                                      .startOf("day")
                                      .isSame(dayjs().startOf("day"), "day")
                                      ? "Due by Today"
                                      : "Due by Tomorrow"}
                                  </h1>
                                )}
                                {/* <p className="text-xs text-gray-400 mt-0">
                                Reminder: {goal.reminder}
                              </p> */}
                              </>
                            )}
                          </div>
                          <div className="flex mt-2 sm:mt-0 items-center gap-2">
                            {goal.completed ? (
                              <>
                                <img
                                  src={trophyIcon}
                                  alt="Trophy"
                                  className="w-7 h-7 mr-1"
                                />
                                <button className="px-4 py-1 bg-emerald-500 text-white rounded-full text-xs sm:text-sm min-w-[120px] text-center flex items-center justify-center gap-1 font-bold">
                                  Completed
                                </button>
                                <div className="relative">
                                  <button
                                    className="px-5 py-1 rounded-full text-xs sm:text-sm border border-gray-300 text-gray-400 cursor-not-allowed text-center opacity-60"
                                    disabled
                                  >
                                    Share
                                  </button>
                                  {false && (
                                    <div className="absolute top-12 right-0 bg-white shadow-xl rounded-xl p-4 flex gap-4 z-50 border border-gray-200 min-w-[220px]">
                                      <div className="absolute -top-2 right-4 w-4 h-4 bg-white border-l border-t border-gray-200 rotate-45"></div>
                                      <a
                                        href={getShareUrl("generic", goal)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                      >
                                        <img
                                          src={shareGeneric}
                                          className="w-7 h-7 hover:scale-110 transition-transform"
                                        />
                                      </a>
                                      <a
                                        href={getShareUrl("instagram", goal)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                      >
                                        <img
                                          src={instagramIcon}
                                          className="w-7 h-7 hover:scale-110 transition-transform"
                                        />
                                      </a>
                                      <a
                                        href={getShareUrl("whatsapp", goal)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                      >
                                        <img
                                          src={whatsappIcon}
                                          className="w-7 h-7 hover:scale-110 transition-transform"
                                        />
                                      </a>
                                      <a
                                        href={getShareUrl("snapchat", goal)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                      >
                                        <img
                                          src={snapchatIcon}
                                          className="w-7 h-7 hover:scale-110 transition-transform"
                                        />
                                      </a>
                                      <a
                                        href={getShareUrl("facebook", goal)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                      >
                                        <img
                                          src={facebookIcon}
                                          className="w-7 h-7 hover:scale-110 transition-transform"
                                        />
                                      </a>
                                    </div>
                                  )}
                                </div>
                              </>
                            ) : (
                              <button
                                className={`px-4 py-1 rounded-full text-xs sm:text-sm text-white transition-colors min-w-[120px] text-center ${
                                  startingGoalId === goal._id
                                    ? "bg-[#0D99FF] cursor-not-allowed"
                                    : "bg-[#0D99FF] hover:bg-blue-600"
                                }`}
                                onClick={() => handleStartGoal(goal)}
                                disabled={startingGoalId === goal._id}
                              >
                                {startingGoalId === goal._id
                                  ? "Preparing..."
                                  : "Start"}
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                </div>
              )}
            </div>

            <div className="border border-gray-300 rounded-lg p-4">
              <div className="flex justify-between items-center mb-2">
                <h3 className="font-bold text-gray-800 text-lg">Recent Wins</h3>
                <button className="text-gray-600">
                  <Share2 size={16} />
                </button>
              </div>
              <div className="space-y-3">
                {isLoadingProgressSummary ? (
                  <div className="flex justify-center py-2">
                    <SimpleThreeDotsLoader message="Fetching achievements..." />
                  </div>
                ) : progressSummaryError ? (
                  <div className="text-red-500 text-sm">
                    {progressSummaryError}
                  </div>
                ) : (
                  <>
                    {/* Challenge Completed Achievement */}
                    {/* {challengeCompletedCount > 0 && (
                    <div className="relative overflow-hidden rounded-lg py-2.5 px-3 flex items-center justify-between bg-gradient-to-r from-amber-50 via-yellow-50 to-amber-50 border border-amber-200 shadow-sm hover:shadow-md transition-all duration-300">
                      <div className="flex items-center gap-2.5 flex-1">
                        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-yellow-500 flex items-center justify-center shadow-md">
                          <span
                            role="img"
                            aria-label="challenge-icon"
                            className="text-lg"
                          >
                            🏆
                          </span>
                        </div>
                        <div className="flex-1">
                          <p className="text-xs md:text-sm font-medium text-gray-800">
                            Great! You have completed{" "}
                            <span className="font-bold text-amber-600 text-sm md:text-base">
                              {challengeCompletedCount}
                            </span>{" "}
                            {challengeCompletedCount === 1
                              ? "challenge"
                              : "challenges"}{" "}
                            recently! Keep it up.
                          </p>
                        </div>
                      </div>
                      <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-amber-200/30 to-transparent rounded-full -mr-12 -mt-12"></div>
                    </div>
                  )} */}

                    {/* Question Answered Achievement */}
                    {questionAnsweredCount > 0 && (
                      <div className="bg-white rounded-xl py-2.5 px-3 flex items-center justify-between border border-gray-200 shadow-sm hover:shadow-md transition-all duration-300">
                        <div className="flex-1">
                          <p className="text-sm md:text-base font-bold text-gray-900 mb-0.5">
                            {questionAnsweredCount}{" "}
                            {questionAnsweredCount === 1
                              ? "Question Solved"
                              : "Questions Solved"}
                          </p>
                          <p
                            className="text-xs md:text-sm font-medium"
                            style={{ color: "#F59E0B" }}
                          >
                            +100 XP
                          </p>
                        </div>
                        <div className="flex-shrink-0 ml-3">
                          <svg
                            width="36"
                            height="36"
                            viewBox="0 0 48 48"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <circle cx="24" cy="24" r="20" fill="#EF4444" />
                            <circle cx="24" cy="24" r="12" fill="#FFFFFF" />
                            <circle cx="24" cy="24" r="6" fill="#3B82F6" />
                            <path
                              d="M24 8L24 16M24 32L24 40M8 24L16 24M32 24L40 24"
                              stroke="#FFFFFF"
                              strokeWidth="2"
                              strokeLinecap="round"
                            />
                          </svg>
                        </div>
                      </div>
                    )}

                    {/* Streak Achievement */}
                    {(() => {
                      if (!streakInfo) return null;

                      const { activeDays, continuousDays, totalDays } =
                        streakInfo;

                      if (activeDays === 0) return null;

                      const streakDays =
                        continuousDays > 0 ? continuousDays : activeDays;
                      const diamondsEarned = Math.round(streakDays * 8.33); // ~50 diamonds for 6 days

                      return (
                        <div className="bg-white rounded-xl py-2.5 px-3 flex items-center justify-between border border-gray-200 shadow-sm hover:shadow-md transition-all duration-300">
                          <div className="flex-1">
                            <p className="text-sm md:text-base font-bold text-gray-900 mb-0.5">
                              {streakDays} Day{streakDays !== 1 ? "s" : ""}{" "}
                              Streak
                            </p>
                            <p className="text-xs md:text-sm font-medium text-gray-900">
                              +50 💎
                            </p>
                          </div>
                          <div className="flex-shrink-0 ml-3 flex items-center justify-center">
                            <span
                              role="img"
                              aria-label="streak-icon"
                              className="text-3xl"
                            >
                              🔥
                            </span>
                          </div>
                        </div>
                      );
                    })()}

                    {/* Completed Tasks */}
                    {/* {completedTasks.length === 0 ? (
                    challengeCompletedCount === 0 &&
                    questionAnsweredCount === 0 &&
                    (!streak ||
                      streak.length === 0 ||
                      getStreakInfo(streak)?.activeDays === 0) ? (
                      <div className="border border-gray-200 rounded-lg py-2.5 px-3 flex items-center justify-center bg-white shadow-sm">
                        <p className="text-xs md:text-sm text-gray-500">
                          No completed tasks yet
                        </p>
                      </div>
                    ) : null
                  ) : (
                    completedTasks.slice(-1).map((task, index) => (
                      <div
                        key={task._id || index}
                        className="relative overflow-hidden rounded-lg py-2.5 px-3 flex items-center justify-between bg-gradient-to-r from-purple-50 via-pink-50 to-purple-50 border border-purple-200 shadow-sm hover:shadow-md transition-all duration-300"
                      >
                        <div className="flex items-center gap-2.5 flex-1">
                          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center shadow-md">
                            <span
                              role="img"
                              aria-label="practice-icon"
                              className="text-lg"
                            >
                              🧑‍🚀
                            </span>
                          </div>
                          <div className="flex-1">
                            <p className="text-xs md:text-sm font-medium text-gray-800">
                              You completed{" "}
                              <span className="font-bold text-purple-600 text-sm md:text-base">
                                {task.totalQuestions || 0}
                              </span>{" "}
                              questions of{" "}
                              <span className="font-semibold text-gray-900">
                                {task.chapter || "Unknown"}
                              </span>{" "}
                              in{" "}
                              <span className="font-semibold text-gray-900">
                                {task.subject || "Unknown"}
                              </span>
                            </p>
                          </div>
                        </div>
                        <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-purple-200/30 to-transparent rounded-full -mr-12 -mt-12"></div>
                      </div>
                    ))
                  )} */}
                    {!hasRecentWins && (
                      <div className="flex items-center justify-center  py-6 px-4 bg-gray-50 text-gray-500 text-sm">
                        No recent wins yet
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
          {/* Goal for the week */}
          {/* <div className="p-4 border-b border-gray-300">
        <div className="flex items-center mb-2">
          <h3 className="font-bold text-gray-800 text-lg">
            Your Goal This Week 🎯
          </h3>
        </div>

        {goalDataError ? (
          <div className="text-red-500 text-sm">{goalDataError}</div>
        ) : isLoadingGoals ? (
          <div className="flex justify-center py-4">
            <SimpleThreeDotsLoader message="Fetching weekly goals..." />
          </div>
        ) : weeklyGoals.length === 0 ? (
          <div className="bg-gray-50 p-3 rounded-lg mb-2">
            <p className="text-gray-500 text-sm">
              No weekly goals set. Set a new goal to get started!
            </p>
          </div>
        ) : (
          weeklyGoals.map((goal, index) => {
            const weeklyShareKey = buildShareKey("week", index);
            return (
              <div
                key={goal._id || index}
                className={`bg-gray-50 p-3 rounded-lg mb-2 ${
                  goal.completed ? "border-2 border-green-400" : "border"
                } flex flex-col sm:flex-row sm:justify-between`}
              >
                <div className="flex flex-col">
                  <p className="font-medium text-gray-800 text-sm">
                    📚 "{goal.subject} test is on{" "}
                    {dayjs(goal.testDate).format("dddd")}!"
                  </p>
                  {goal.completed ? (
                    <div className="text-green-600 text-xs sm:text-sm mb-1">
                      Completed on{" "}
                      {new Date(goal.completed_at).toLocaleString("en-US", {
                        year: "numeric",
                        month: "short",
                        day: "2-digit",
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: true,
                      })}
                    </div>
                  ) : null}
                  <div className="w-full bg-purple-200 h-2 rounded-full">
                    <div
                      className={`bg-primary h-2 rounded-full ${
                        goal.completed ? "w-full" : "w-3/4"
                      }`}
                    ></div>
                  </div>
                  <p className="text-xs text-gray-600 mt-1">
                    {goal.completed
                      ? "Congrats, You smashed it"
                      : "You can smash these! Practice more."}
                  </p>
                </div>
                <div className="flex mt-2 sm:mt-0 items-center gap-2">
                  {goal.completed ? (
                    <>
                      <img
                        src={trophyIcon}
                        alt="Trophy"
                        className="w-7 h-7 mr-1"
                      />
                      <button className="px-4 py-1 bg-green-400 text-white rounded-full text-xs sm:text-sm min-w-[90px] text-center flex items-center gap-1">
                        Completed
                      </button>
                      <div className="relative">
                        <button
                          className="px-5 py-1 rounded-full text-xs sm:text-sm border border-gray-300 hover:bg-gray-100 text-center"
                          onClick={() =>
                            setShowSharePopover(
                              showSharePopover === weeklyShareKey
                                ? null
                                : weeklyShareKey
                            )
                          }
                        >
                          Share
                        </button>
                        {showSharePopover === weeklyShareKey && (
                          <div className="absolute top-12 right-0 bg-white shadow-xl rounded-xl p-4 flex gap-4 z-50 border border-gray-200 min-w-[220px]">
                            <div className="absolute -top-2 right-4 w-4 h-4 bg-white border-l border-t border-gray-200 rotate-45"></div>
                            <a
                              href={getShareUrl("generic", goal)}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <img
                                src={shareGeneric}
                                className="w-7 h-7 hover:scale-110 transition-transform"
                              />
                            </a>
                            <a
                              href={getShareUrl("instagram", goal)}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <img
                                src={instagramIcon}
                                className="w-7 h-7 hover:scale-110 transition-transform"
                              />
                            </a>
                            <a
                              href={getShareUrl("whatsapp", goal)}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <img
                                src={whatsappIcon}
                                className="w-7 h-7 hover:scale-110 transition-transform"
                              />
                            </a>
                            <a
                              href={getShareUrl("snapchat", goal)}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <img
                                src={snapchatIcon}
                                className="w-7 h-7 hover:scale-110 transition-transform"
                              />
                            </a>
                            <a
                              href={getShareUrl("facebook", goal)}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <img
                                src={facebookIcon}
                                className="w-7 h-7 hover:scale-110 transition-transform"
                              />
                            </a>
                          </div>
                        )}
                      </div>
                    </>
                  ) : (
                    <button
                      onClick={() => handleStartGoal(goal)}
                      disabled={startingGoalId === goal._id}
                      className={`px-4 py-1 bg-blue-500 text-white rounded-full text-xs sm:text-sm min-w-[120px] text-center flex items-center justify-center gap-1 transition-colors ${
                        startingGoalId === goal._id
                          ? "opacity-75 cursor-not-allowed"
                          : "hover:bg-blue-600"
                      }`}
                    >
                      {startingGoalId === goal._id ? "Preparing..." : "Start"}
                    </button>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div> */}
          {/* Challenge banner */}

          <div className="relative bg-primary text-white overflow-visible flex flex-col md:flex-row mb-10">
            <div className="relative w-full md:w-2/5 rounded-t-3xl md:rounded-l-3xl md:rounded-tr-none overflow-visible order-first md:order-none">
              <img
                src="/images/leaderboardBanner.png"
                alt="Banner illustration"
                className="relative z-10 w-full h-48 md:h-full object-contain"
              />
            </div>
            <div className="w-full md:w-3/5 py-4 px-6 flex flex-col justify-center text-center md:text-left">
              <h2 className="text-2xl md:text-4xl font-sans text-white mb-3 md:mb-5">
                Challenge your buddy and learn together!
              </h2>
              <div className="space-y-2">
                <button
                  className="ml-13 bg-white text-primary px-2 py-3 rounded-full text-sm font-medium w-full md:w-80"
                  onClick={handleInviteButton}
                >
                  Invite Your Friend
                </button>
                <p className="p-2 md:p-5 text-base md:text-xl text-white">
                  Earn points and grow your strength areas
                </p>
              </div>
            </div>
          </div>
          {pendingChallenges.length > 0 &&
            pendingChallenges.map((challenge) => (
              <ChallengeInviteCard
                key={challenge.id}
                challenge={challenge}
                onJoin={handleJoinChallenge}
              />
            ))}

          {/* AdventurePath Component */}
          {/* <AdventurePath /> */}
          {/* Bottom section */}

          {/* Leaderboard section */}
          {/* <div className="p-4 md:p-6 border-b border-gray-300">
        <div className="flex justify-between items-center mb-3 md:mb-5">
          <h2 className="font-bold text-lg md:text-xl text-gray-800">
            Leaderboard
          </h2>
        </div>

        <div className="space-y-4">
          {loading ? (
            <div className="flex justify-center py-5 md:py-10">
              <p className="text-gray-500">Loading leaderboard data...</p>
            </div>
          ) : error ? (
            <div className="bg-red-50 p-4 rounded-lg">
              <p className="text-red-500 font-medium">Error: {error}</p>
              <p className="text-sm text-red-400 mt-1">
                Please check your API connection
              </p>
            </div>
          ) : leaderboard && leaderboard.length === 0 ? (
            <div className="text-center py-5 md:py-10">
              <p className="text-gray-500">
                No leaderboard data available. Start improving your levels to
                appear here!
              </p>
            </div>
          ) : (
            leaderboard &&
            leaderboard.slice(0, visibleCount).map((item, index) => (
              <div
                key={index}
                className="bg-gray-50 p-3 md:p-4 rounded-lg flex flex-col sm:flex-row justify-between items-start sm:items-center hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-center flex-1 mb-2 sm:mb-0">
                  <div className="w-[6.75rem] h-[6.75rem] md:w-[6.75rem] md:h-[6.75rem] rounded-full mr-3 md:mr-4 flex items-center justify-center overflow-hidden flex-shrink-0">
                    {item.profilePhoto ? (
                      <img
                        src={item.profilePhoto}
                        alt={`${item.name}'s avatar`}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div
                        className={`w-full h-full ${getAvatarColor(
                          item.subject
                        )} flex items-center justify-center`}
                      >
                        <span className="text-sm font-medium">
                          {getInitials(item.name)}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="flex-1">
                    <p className="text-sm md:text-base font-medium text-gray-800">
                      {getLeaderboardMessage(item)}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      Last updated: {new Date(item.timestamp).toLocaleString()}
                    </p>
                  </div>
                </div>
                <div className="flex items-center bg-white px-2 py-1 rounded-full shadow-sm self-end sm:self-auto">
                  <span className="text-gray-700 text-sm md:text-base font-medium mr-1">
                    {item.score}
                  </span>
                  <BarChart2 size={16} className="text-gray-600" />
                </div>
              </div>
            ))
          )}
        </div>

        <div className="mt-4 text-center">
          <button
            onClick={() => setVisibleCount((prev) => prev + 10)}
            className="text-purple-600 text-sm font-medium hover:text-purple-800 transition-colors"
          >
            View more
          </button>
        </div>
      </div>
 */}
          {isJoining && (
            <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-40">
              <div className="bg-white rounded-xl shadow-lg p-8 text-center max-w-sm sm:max-w-md w-full flex flex-col items-center">
                <svg
                  className="animate-spin h-8 w-8 sm:h-10 sm:w-10 text-indigo-600 mb-4 sm:mb-6"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v8z"
                  ></path>
                </svg>
                <h2 className="text-lg sm:text-xl font-bold text-indigo-700 mb-2 w-full">
                  Preparing your challenge...
                </h2>
                <h4 className="text-sm sm:text-base font-bold text-indigo-700 w-full">
                  Please wait while we set things up for you.
                </h4>
              </div>
            </div>
          )}
        </div>
      )}
      <RateLimitModal
        isOpen={isRateLimitModalOpen}
        serverMessage={rateLimitServerMessage}
        supportMessage={GOAL_RATE_LIMIT_SUPPORT_MESSAGE}
        onClose={handleRateLimitModalClose}
      />
    </>
  );
}
